import {
  Avatar,
  Box,
  Button,
  IconButton,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  SearchIcon,
  LockIcon,
  CopyIcon,
  RepeatIcon,
} from "@chakra-ui/icons";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { CrownIcon, TrashIcon, PaletteIcon, Ornament } from "../components/ui/Icons";
import { copyText } from "../components/ui/clipboard";

// пароль без похожих символов — чтобы диктовать по телефону
const generatePassword = () => {
  const abc = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from(
    { length: 10 },
    () => abc[Math.floor(Math.random() * abc.length)]
  ).join("");
};

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const tabStyle = {
  fontFamily: "'Prata', serif",
  fontSize: "15px",
  letterSpacing: "0.1em",
  color: "rgba(36, 61, 39, 0.55)",
  pb: 3,
  px: 6,
  borderBottom: "2px solid transparent",
  transition: "all 0.2s ease",
  _hover: { color: "#243D27" },
  _selected: { color: "#8D414B", borderBottomColor: "#8D414B" },
  _focus: { boxShadow: "none" },
};

// Кнопка удаления с подтверждением вторым кликом
const ConfirmDelete = ({ onConfirm, label = "Удалить" }) => {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);

  return armed ? (
    <Button
      size="xs"
      colorScheme="burgundy"
      borderRadius="8px"
      onClick={onConfirm}
    >
      Точно?
    </Button>
  ) : (
    <Tooltip label={label} hasArrow>
      <IconButton
        aria-label={label}
        icon={<TrashIcon />}
        size="xs"
        variant="ghost"
        colorScheme="burgundy"
        borderRadius="8px"
        onClick={() => setArmed(true)}
      />
    </Tooltip>
  );
};

const StatTile = ({ label, value, hint }) => (
  <Box
    flex="1"
    minW={{ base: "45%", md: "0" }}
    bg="#FBF0D9"
    border="1px solid #E4D2A6"
    borderRadius="16px"
    p={4}
    textAlign="center"
    boxShadow="0 10px 30px -18px rgba(36, 61, 39, 0.3)"
  >
    <Text
      fontFamily="'Prata', serif"
      fontSize="30px"
      color="#243D27"
      lineHeight="1.1"
    >
      {value ?? "—"}
    </Text>
    <Text fontSize="13px" color="rgba(36, 61, 39, 0.65)" mt={1}>
      {label}
    </Text>
    {hint && (
      <Text fontSize="11px" color="#8D414B" mt={0.5}>
        {hint}
      </Text>
    )}
  </Box>
);

const AdminPage = () => {
  const history = useHistory();
  const toast = useToast();
  const [me] = useState(() => JSON.parse(localStorage.getItem("userInfo")));

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [chats, setChats] = useState(null);
  const [search, setSearch] = useState("");
  const [passTarget, setPassTarget] = useState(null); // кому меняем пароль
  const [newPass, setNewPass] = useState("");
  const [passSaving, setPassSaving] = useState(false);
  const [passDone, setPassDone] = useState(false);

  const config = useCallback(
    () => ({ headers: { Authorization: `Bearer ${me?.token}` } }),
    [me]
  );

  const fail = useCallback(
    (error, title) => {
      if (error.response?.status === 403) {
        history.push("/chats");
        return;
      }
      toast({
        title,
        description: error.response?.data?.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    },
    [history, toast]
  );

  const loadAll = useCallback(async () => {
    try {
      const cfg = config();
      const [s, u, c] = await Promise.all([
        axios.get("/api/admin/stats", cfg),
        axios.get("/api/admin/users", cfg),
        axios.get("/api/admin/chats", cfg),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setChats(c.data);
    } catch (error) {
      fail(error, "Не удалось загрузить данные");
    }
  }, [config, fail]);

  useEffect(() => {
    if (!me) {
      history.push("/");
      return;
    }
    // права проверяет сервер: не-админа отсюда выкинет 403 в fail()
    loadAll();
  }, [me, history, loadAll]);

  const toggleAdmin = async (u) => {
    try {
      const { data } = await axios.put(
        `/api/admin/users/${u._id}/admin`,
        {},
        config()
      );
      setUsers(
        users.map((x) =>
          x._id === u._id ? { ...x, isAdmin: data.isAdmin } : x
        )
      );
    } catch (error) {
      fail(error, "Не удалось изменить права");
    }
  };

  const openPasswordDialog = (u) => {
    setPassTarget(u);
    setNewPass(generatePassword());
    setPassDone(false);
  };

  const savePassword = async () => {
    if (!newPass || newPass.length < 6) {
      toast({
        title: "Пароль не короче 6 символов",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    try {
      setPassSaving(true);
      await axios.put(
        `/api/admin/users/${passTarget._id}/password`,
        { password: newPass },
        config()
      );
      setPassDone(true);
    } catch (error) {
      fail(error, "Не удалось изменить пароль");
    } finally {
      setPassSaving(false);
    }
  };

  const removeUser = async (u) => {
    try {
      await axios.delete(`/api/admin/users/${u._id}`, config());
      toast({
        title: `${u.name} удалён из клуба`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
      loadAll();
    } catch (error) {
      fail(error, "Не удалось удалить");
    }
  };

  const removeChat = async (c) => {
    try {
      await axios.delete(`/api/admin/chats/${c._id}`, config());
      toast({
        title: "Беседа удалена",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
      loadAll();
    } catch (error) {
      fail(error, "Не удалось удалить беседу");
    }
  };

  const filteredUsers = users?.filter(
    (u) =>
      !search ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
  );

  const rowStyle = {
    d: "flex",
    alignItems: "center",
    bg: "#FBF0D9",
    border: "1px solid #E4D2A6",
    borderRadius: "14px",
    px: 4,
    py: 3,
    mb: 2,
  };

  return (
    <Box w="100%" minH="100vh">
      {/* Шапка */}
      <Box
        d="flex"
        justifyContent="space-between"
        alignItems="center"
        bg="#243D27"
        color="#F2DFB6"
        p="10px 18px"
        boxShadow="0 4px 18px rgba(20, 35, 23, 0.28)"
      >
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          color="#F2DFB6"
          borderRadius="12px"
          _hover={{ bg: "rgba(242, 223, 182, 0.12)" }}
          onClick={() => history.push("/chats")}
        >
          К чатам
        </Button>
        <Box d="flex" alignItems="baseline">
          <Text className="atika-wordmark" fontSize="27px" lineHeight="1">
            А.Т.И.К.А
          </Text>
          <Text
            fontFamily="'Prata', serif"
            fontSize="12px"
            color="#ECCFA7"
            letterSpacing="0.3em"
            ml={3}
          >
            АДМИНКА
          </Text>
        </Box>
        <Avatar size="sm" name={me?.name} src={me?.pic} />
      </Box>

      <Box maxW="960px" mx="auto" p={{ base: 4, md: 6 }}>
        {/* Статистика */}
        <Box d="flex" flexWrap="wrap" style={{ gap: "12px" }} mb={6}>
          <StatTile label="художников" value={stats?.users} />
          <StatTile
            label="бесед"
            value={stats?.chats}
            hint={stats ? `групповых: ${stats.groupChats}` : null}
          />
          <StatTile label="сообщений" value={stats?.messages} />
          <StatTile label="сообщений сегодня" value={stats?.messagesToday} />
        </Box>

        <Tabs variant="unstyled">
          <TabList borderBottom="1px solid #E4D2A6" mb={5}>
            <Tab {...tabStyle}>ХУДОЖНИКИ</Tab>
            <Tab {...tabStyle}>БЕСЕДЫ</Tab>
          </TabList>

          <TabPanels>
            {/* Художники */}
            <TabPanel p={0}>
              <Box d="flex" mb={4} maxW="360px">
                <Input
                  placeholder="Имя или почта"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <IconButton
                  aria-label="Поиск"
                  icon={<SearchIcon />}
                  ml={2}
                  borderRadius="12px"
                  bg="#F6E8C6"
                  color="#243D27"
                  _hover={{ bg: "#EAD9AC" }}
                />
              </Box>

              {!filteredUsers ? (
                <Spinner color="#8D414B" size="lg" d="block" mx="auto" my={10} />
              ) : (
                filteredUsers.map((u) => (
                  <Box key={u._id} {...rowStyle}>
                    <Avatar size="sm" name={u.name} src={u.pic} mr={3} />
                    <Box flex="1" minW={0}>
                      <Box d="flex" alignItems="center">
                        <Text fontWeight={500} isTruncated>
                          {u.name}
                        </Text>
                        {u.isAdmin && (
                          <Box
                            d="inline-flex"
                            alignItems="center"
                            bg="#243D27"
                            color="#F2DFB6"
                            fontSize="11px"
                            px={2}
                            py={0.5}
                            borderRadius="full"
                            ml={2}
                          >
                            <CrownIcon size={11} />
                            <Text ml={1}>админ</Text>
                          </Box>
                        )}
                        {me?._id === u._id && (
                          <Text fontSize="11px" color="#8D414B" ml={2}>
                            это вы
                          </Text>
                        )}
                      </Box>
                      <Text fontSize="xs" opacity={0.7} isTruncated>
                        {u.email} · в клубе с {formatDate(u.createdAt)} ·
                        сообщений: {u.messageCount}
                      </Text>
                    </Box>
                    <Box d="flex" alignItems="center" ml={2}>
                      <Tooltip label="Задать новый пароль" hasArrow>
                        <IconButton
                          aria-label="Сменить пароль"
                          icon={<LockIcon boxSize="12px" />}
                          size="xs"
                          variant="ghost"
                          color="rgba(36,61,39,0.5)"
                          borderRadius="8px"
                          mr={1}
                          onClick={() => openPasswordDialog(u)}
                        />
                      </Tooltip>
                    </Box>
                    {me?._id !== u._id && (
                      <Box d="flex" alignItems="center">
                        <Tooltip
                          label={u.isAdmin ? "Снять админа" : "Сделать админом"}
                          hasArrow
                        >
                          <IconButton
                            aria-label="Права админа"
                            icon={<CrownIcon />}
                            size="xs"
                            variant="ghost"
                            color={u.isAdmin ? "#8D414B" : "rgba(36,61,39,0.45)"}
                            borderRadius="8px"
                            mr={1}
                            onClick={() => toggleAdmin(u)}
                          />
                        </Tooltip>
                        <ConfirmDelete
                          label="Удалить из клуба"
                          onConfirm={() => removeUser(u)}
                        />
                      </Box>
                    )}
                  </Box>
                ))
              )}
            </TabPanel>

            {/* Беседы */}
            <TabPanel p={0}>
              {!chats ? (
                <Spinner color="#8D414B" size="lg" d="block" mx="auto" my={10} />
              ) : (
                chats.map((c) => (
                  <Box key={c._id} {...rowStyle}>
                    <Box
                      w="36px"
                      h="36px"
                      minW="36px"
                      borderRadius="full"
                      bg={c.isGroupChat ? "#243D27" : "#8D414B"}
                      d="flex"
                      alignItems="center"
                      justifyContent="center"
                      mr={3}
                    >
                      <PaletteIcon size={18} color="#F2DFB6" />
                    </Box>
                    <Box flex="1" minW={0}>
                      <Text fontWeight={500} isTruncated>
                        {c.isGroupChat
                          ? c.chatName
                          : c.users.map((u) => u.name).join(" ↔ ")}
                        <Text
                          as="span"
                          fontSize="11px"
                          color="#8D414B"
                          ml={2}
                        >
                          {c.isGroupChat
                            ? c.isPrivate
                              ? "группа · закрытая"
                              : "группа · открытая"
                            : "личная"}
                        </Text>
                      </Text>
                      <Text fontSize="xs" opacity={0.7} isTruncated>
                        участников: {c.users.length} · сообщений:{" "}
                        {c.messageCount}
                        {c.isGroupChat && c.groupAdmin
                          ? ` · создатель: ${c.groupAdmin.name}`
                          : ""}
                        {c.latestMessage
                          ? ` · «${c.latestMessage.content.slice(0, 40)}»`
                          : ""}
                      </Text>
                    </Box>
                    <ConfirmDelete
                      label="Удалить беседу"
                      onConfirm={() => removeChat(c)}
                    />
                  </Box>
                ))
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>

        <Box d="flex" justifyContent="center" mt={8} mb={4}>
          <Ornament color="#8D414B" opacity={0.4} />
        </Box>
      </Box>

      {/* Смена пароля участнику — пока не работает восстановление по почте */}
      <Modal
        isOpen={!!passTarget}
        onClose={() => setPassTarget(null)}
        isCentered
        size="sm"
      >
        <ModalOverlay bg="rgba(20, 35, 23, 0.45)" />
        <ModalContent>
          <ModalHeader
            fontSize="20px"
            fontFamily="'Prata', serif"
            color="#243D27"
            pb={1}
          >
            Новый пароль
          </ModalHeader>
          <ModalCloseButton borderRadius="10px" />
          <ModalBody pb={2}>
            <Text fontSize="sm" color="rgba(36,61,39,0.7)" mb={3}>
              Для художника{" "}
              <Text as="span" fontWeight={600} color="#243D27">
                {passTarget?.name}
              </Text>
              {passDone
                ? " — пароль изменён. Передайте его лично или в мессенджере."
                : ". Пароль придётся передать человеку самому — почта на сервере пока не подключена."}
            </Text>

            <Box d="flex">
              <Input
                value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
                isReadOnly={passDone}
                fontFamily="monospace"
                fontSize="15px"
                borderRadius="10px"
              />
              <Tooltip label="Скопировать" hasArrow>
                <IconButton
                  aria-label="Скопировать"
                  icon={<CopyIcon />}
                  ml={2}
                  borderRadius="10px"
                  bg="#243D27"
                  color="#F2DFB6"
                  _hover={{ bg: "#2E4A33" }}
                  onClick={() =>
                    copyText(newPass).then(() =>
                      toast({
                        title: "Пароль скопирован",
                        status: "success",
                        duration: 2000,
                        isClosable: true,
                        position: "bottom",
                      })
                    )
                  }
                />
              </Tooltip>
              {!passDone && (
                <Tooltip label="Сгенерировать другой" hasArrow>
                  <IconButton
                    aria-label="Другой пароль"
                    icon={<RepeatIcon />}
                    ml={2}
                    borderRadius="10px"
                    variant="outline"
                    colorScheme="pine"
                    onClick={() => setNewPass(generatePassword())}
                  />
                </Tooltip>
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            {passDone ? (
              <Button
                size="sm"
                colorScheme="burgundy"
                onClick={() => setPassTarget(null)}
              >
                Готово
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="pine"
                  mr={2}
                  onClick={() => setPassTarget(null)}
                >
                  Отмена
                </Button>
                <Button
                  size="sm"
                  colorScheme="burgundy"
                  isLoading={passSaving}
                  onClick={savePassword}
                  _hover={{ bg: "#7A3841" }}
                >
                  Сохранить пароль
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminPage;
